import { useSyncExternalStore } from "react";

/**
 * Enquiry Store - separate from orders
 * Enquiries have their own numbering (ENQ-...) and no invoice/staff assignment
 * until explicitly converted to an order.
 */

import { addOrder, type Order, type OrderSource } from "./orderStore";

export interface EnquiryNote {
  id: string;
  text: string;
  timestamp: number;
  author?: string;
}

export interface Enquiry {
  id: string;
  enquiryId: string; // ENQ-YYYYMMDD-XXXX
  deviceName: string;
  variant: string;
  grade: string;
  screenType?: string;
  color?: string;
  customerName: string;
  customerPhone?: string;
  location: string;
  message?: string;
  timestamp: number;
  status: "open" | "responded" | "converted" | "closed";
  orderSource?: OrderSource;
  referredBy?: string;
  notes?: EnquiryNote[];
  assignedTo?: string; // user_id of assigned staff/admin
}

const ENQUIRIES_KEY = "khays_enquiries";
const ENQUIRIES_EVENT = "khays-enquiries-updated";
let enquiriesRevision = 0;

function emitEnquiriesChanged(): void {
  enquiriesRevision += 1;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(ENQUIRIES_EVENT));
  }
}

function subscribeEnquiries(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(ENQUIRIES_EVENT, listener);
  return () => window.removeEventListener(ENQUIRIES_EVENT, listener);
}

export function useEnquiriesRevision(): number {
  return useSyncExternalStore(subscribeEnquiries, () => enquiriesRevision, () => enquiriesRevision);
}

function generateEnquiryId(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `ENQ-${dateStr}-${rand}`;
}

export function loadEnquiries(): Enquiry[] {
  try {
    const stored = localStorage.getItem(ENQUIRIES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

export function saveEnquiries(enquiries: Enquiry[]): void {
  localStorage.setItem(ENQUIRIES_KEY, JSON.stringify(enquiries));
  emitEnquiriesChanged();
}

export function addEnquiry(data: Omit<Enquiry, "id" | "enquiryId" | "timestamp" | "status">): Enquiry {
  const enquiries = loadEnquiries();
  const enquiry: Enquiry = {
    ...data,
    id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    enquiryId: generateEnquiryId(),
    timestamp: Date.now(),
    status: "open",
  };
  enquiries.unshift(enquiry);
  saveEnquiries(enquiries);
  // Fire-and-forget: route a targeted admin notification to the referred staff
  // (or as a global notification if no referral).
  void notifyAdminsOfEnquiry(enquiry);
  return enquiry;
}

async function notifyAdminsOfEnquiry(enquiry: Enquiry): Promise<void> {
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-admins`;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!url || !anonKey) return;
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${anonKey}` },
      body: JSON.stringify({
        type: "enquiry",
        title: "New Enquiry",
        body: `${enquiry.customerName} — ${enquiry.deviceName}${enquiry.variant ? ` ${enquiry.variant}` : ""}`,
        targetUsername: enquiry.referredBy || null,
        metadata: { enquiryId: enquiry.enquiryId, deviceName: enquiry.deviceName, referredBy: enquiry.referredBy },
      }),
    });
  } catch {
    // best-effort; never block the visitor
  }
}

export function updateEnquiryStatus(enquiryId: string, status: Enquiry["status"]): void {
  const enquiries = loadEnquiries();
  const idx = enquiries.findIndex(e => e.id === enquiryId);
  if (idx >= 0) {
    enquiries[idx].status = status;
    saveEnquiries(enquiries);
  }
}

export function updateEnquiryField(enquiryId: string, updates: Partial<Enquiry>): void {
  const enquiries = loadEnquiries();
  const idx = enquiries.findIndex(e => e.id === enquiryId);
  if (idx >= 0) {
    enquiries[idx] = { ...enquiries[idx], ...updates };
    saveEnquiries(enquiries);
  }
}

export function addEnquiryNote(enquiryId: string, text: string, author?: string): void {
  const enquiries = loadEnquiries();
  const idx = enquiries.findIndex(e => e.id === enquiryId);
  if (idx >= 0) {
    if (!enquiries[idx].notes) enquiries[idx].notes = [];
    enquiries[idx].notes!.push({
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      text,
      timestamp: Date.now(),
      author,
    });
    saveEnquiries(enquiries);
  }
}

export function deleteEnquiryNote(enquiryId: string, noteId: string): void {
  const enquiries = loadEnquiries();
  const idx = enquiries.findIndex(e => e.id === enquiryId);
  if (idx >= 0 && enquiries[idx].notes) {
    enquiries[idx].notes = enquiries[idx].notes!.filter(n => n.id !== noteId);
    saveEnquiries(enquiries);
  }
}

export function deleteEnquiry(enquiryId: string): void {
  saveEnquiries(loadEnquiries().filter(e => e.id !== enquiryId));
}

/** Convert an enquiry to an order */
export function convertEnquiryToOrder(enquiryId: string, finalPrice: number, shippingFee?: number, shippingLabel?: string): Order | null {
  const enquiries = loadEnquiries();
  const enquiry = enquiries.find(e => e.id === enquiryId);
  if (!enquiry) return null;

  const order = addOrder({
    deviceName: enquiry.deviceName,
    variant: enquiry.variant,
    grade: enquiry.grade,
    screenType: enquiry.screenType,
    color: enquiry.color,
    finalPrice,
    customerName: enquiry.customerName,
    customerPhone: enquiry.customerPhone,
    location: enquiry.location,
    smsOptIn: true,
    orderSource: enquiry.orderSource || "enquiry",
    referredBy: enquiry.referredBy,
    shippingFee,
    shippingLabel,
    status: "Pending",
  });

  // Mark enquiry as converted
  updateEnquiryStatus(enquiryId, "converted");

  return order;
}

/** Migrate existing "Enquiry" status orders to the enquiry store */
export function migrateEnquiryOrders(orders: Order[]): { migratedIds: string[] } {
  const enquiryOrders = orders.filter(o => o.status === "Enquiry");
  if (enquiryOrders.length === 0) return { migratedIds: [] };

  const existing = loadEnquiries();
  const existingOrderIds = new Set(existing.map(e => e.id));
  const migratedIds: string[] = [];

  for (const order of enquiryOrders) {
    if (existingOrderIds.has(order.id)) continue;
    existing.unshift({
      id: order.id,
      enquiryId: `ENQ-${new Date(order.timestamp).toISOString().slice(0, 10).replace(/-/g, "")}-${order.invoiceId.slice(-4)}`,
      deviceName: order.deviceName,
      variant: order.variant,
      grade: order.grade,
      screenType: order.screenType,
      color: order.color,
      customerName: order.customerName,
      customerPhone: order.customerPhone,
      location: order.location,
      timestamp: order.timestamp,
      status: "open",
      orderSource: order.orderSource,
      referredBy: order.referredBy,
    });
    migratedIds.push(order.id);
  }

  if (migratedIds.length > 0) saveEnquiries(existing);
  return { migratedIds };
}

export const ENQUIRY_STATUSES: Enquiry["status"][] = ["open", "responded", "converted", "closed"];
